import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plane, Building2, CalendarClock, AlertTriangle, ArrowRight, ShieldCheck,
  Sparkles, ScanLine, FileCheck2, MessageSquare, Star, CheckCircle2,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SERVICES, OFFICES, CONTACT } from "@/data/services";

const ICONS = { Plane, Building2, CalendarClock, AlertTriangle };

const STEPS = [
  { icon: ScanLine, t: "Upload passport", d: "Our AI reads your bio-data page and fills the form in seconds." },
  { icon: FileCheck2, t: "We review & lodge", d: "Consultants verify eligibility and lodge with Immigration." },
  { icon: MessageSquare, t: "Track & pay", d: "Get a quotation, pay securely online and message our team." },
];

const STATS = [
  { n: "90-day", l: "e-visa validity supported" },
  { n: "2", l: "offices — Hanoi & HCMC" },
  { n: "4", l: "core immigration services" },
  { n: "100%", l: "regulation-compliant filings" },
];

export default function Home() {
  const hero = SERVICES[0];
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white grain">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] bg-brand-red/5 rounded-full blur-3xl" />
        <div className="absolute top-40 -left-40 w-[420px] h-[420px] bg-brand-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center relative">
          <div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-brand-navy text-balance">
              Your gateway to <span className="text-brand-red">Vietnam</span>, handled with precision.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Vietnam visa applications, business registration, visa extensions and overstay resolution — prepared by professionals in full compliance with Vietnamese immigration law.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-start-btn"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-red text-white font-semibold shadow-redglow hover:bg-brand-reddark transition-colors">
                Start your application <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/services" data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border-2 border-slate-200 text-brand-navy font-semibold hover:border-brand-navy transition-colors">
                Explore services
              </Link>
            </motion.div>
            <div className="mt-9 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-brand-red" /> Regulation-compliant</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-brand-gold fill-brand-gold" /> Trusted consultancy</span>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="relative">
            <div className="relative rounded-[28px] overflow-hidden shadow-lift border border-slate-200">
              <img src={hero.hero} alt="Vietnam visa" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4 flex items-center gap-3 border border-white/40">
                <div className="w-11 h-11 rounded-xl bg-brand-red flex items-center justify-center text-white"><ScanLine className="w-5 h-5" /></div>
                <div>
                  <p className="text-sm font-bold text-brand-navy">Passport auto-fill</p>
                  <p className="text-xs text-slate-600">Upload once — we read your details instantly.</p>
                </div>
              </div>
            </div>
            <div className="absolute -top-5 -left-5 bg-white rounded-2xl shadow-lift border border-slate-200 px-4 py-3 animate-float-slow hidden sm:block">
              <p className="text-xs text-slate-500">Fastest e-visa</p>
              <p className="text-lg font-bold text-brand-navy">3–5 days</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS marquee-ish band */}
      <section className="bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08}>
              <p className="font-serif text-4xl text-brand-gold">{s.n}</p>
              <p className="text-sm text-slate-400 mt-1">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES bento */}
      <section className="max-w-7xl mx-auto px-6 py-24" id="services">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-brand-red font-bold mb-3">Our services</p>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-navy tracking-tight max-w-2xl">Everything you need to live, work and invest in Vietnam</h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Plane;
            const big = i === 0;
            return (
              <Reveal key={s.key} delay={i * 0.08}
                className={big ? "md:col-span-7 md:row-span-2" : "md:col-span-5"}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.key}`}
                  className="group block h-full rounded-3xl border border-slate-200 bg-white overflow-hidden hover:-translate-y-1 hover:shadow-lift transition-all duration-300">
                  <div className={`relative ${big ? "h-56" : "h-36"} overflow-hidden`}>
                    <img src={s.hero} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent" />
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center text-brand-red">
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className={`font-serif ${big ? "text-3xl" : "text-2xl"} text-brand-navy tracking-tight`}>{s.title}</h3>
                    <p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.short}</p>
                    {big && (
                      <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                        {s.features.slice(0, 4).map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle2 className="w-4 h-4 text-brand-red shrink-0 mt-0.5" /> {f}
                          </li>
                        ))}
                      </ul>
                    )}
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red group-hover:gap-2.5 transition-all">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-red font-bold mb-3">How it works</p>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-navy tracking-tight">Three steps to peace of mind</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {STEPS.map((st, i) => (
              <Reveal key={st.t} delay={i * 0.1}>
                <div className="relative bg-white rounded-3xl border border-slate-200 p-8 h-full hover:shadow-soft transition-shadow">
                  <span className="absolute top-6 right-7 font-serif text-6xl text-slate-100 select-none">{i + 1}</span>
                  <div className="w-12 h-12 rounded-2xl bg-brand-red/10 flex items-center justify-center text-brand-red mb-5"><st.icon className="w-6 h-6" /></div>
                  <h3 className="font-semibold text-xl text-brand-navy">{st.t}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{st.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-[32px] bg-brand-navy text-white p-10 md:p-16">
            <div className="absolute -right-20 -bottom-24 w-96 h-96 bg-brand-red/20 rounded-full blur-3xl" />
            <div className="absolute right-10 top-8 w-40 h-40 bg-brand-gold/10 rounded-full blur-2xl" />
            <div className="relative max-w-2xl">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Ready to begin your Vietnam journey?</h2>
              <p className="mt-4 text-slate-300 text-lg">Create an account, upload your passport, and let our consultants handle the paperwork — accurately and on time.</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/register" data-testid="cta-register-btn" className="px-7 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow">Create free account</Link>
                <a href={`tel:${CONTACT.phone}`} className="px-7 py-3.5 rounded-full bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-colors">Call {CONTACT.phoneDisplay}</a>
              </div>
              <p className="mt-8 text-sm text-slate-400 flex flex-wrap gap-x-6 gap-y-1">
                {OFFICES.map((o) => <span key={o.city}><strong className="text-white">{o.city}:</strong> {o.address}</span>)}
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
